import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  CatalogValidationError,
  useLaborRateTiers,
  useSaveLaborRateTiers,
  type LaborRateTiers,
} from '@/routes/pricebook/usePricebook';

type TierField = 'overtimeMultiplier' | 'emergencyMultiplier';

/**
 * The account's labor rate tiers (M2-33; Kyle, Q1 = A): Standard 1×, and the
 * Overtime and Emergency multipliers every time entry at that tier bills at.
 * On the price book page because they multiply its labor items.
 *
 * Read by whoever reads the book; changed by `pricebook.manage` (Owner and
 * Admin) — anyone else sees the figures with no form, never a control that
 * then 403s. The API's rule (1× to 10×, two decimals) is restated so the
 * office hears it before the round trip; the server's sentence still wins.
 */
export function LaborRateTiersPanel({
  canManage,
  onAnnounce,
}: {
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const tiers = useLaborRateTiers();
  const [editing, setEditing] = useState(false);

  return (
    <div className="rounded-lg border p-4" data-testid="labor-rate-tiers">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('pricebook.tiers.heading')}</h2>
        {canManage && tiers.data && !editing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t('pricebook.tiers.edit')}
          </Button>
        ) : null}
      </div>
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {t('pricebook.tiers.description')}
      </p>
      {tiers.isError ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-destructive)]">
          {t('pricebook.tiers.loadFailed')}
        </p>
      ) : null}
      {tiers.data && !editing ? (
        <dl className="mt-3 grid grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('timeEntries.tier.Standard')}
            </dt>
            <dd data-testid="tier-standard">×1</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('timeEntries.tier.Overtime')}
            </dt>
            <dd data-testid="tier-overtime">×{Number(tiers.data.overtimeMultiplier)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('timeEntries.tier.Emergency')}
            </dt>
            <dd data-testid="tier-emergency">×{Number(tiers.data.emergencyMultiplier)}</dd>
          </div>
        </dl>
      ) : null}
      {tiers.data && editing ? (
        <TiersForm
          tiers={tiers.data}
          onDone={(saved) => {
            setEditing(false);
            if (saved) onAnnounce(t('pricebook.tiers.saved'));
          }}
        />
      ) : null}
    </div>
  );
}

function TiersForm({ tiers, onDone }: { tiers: LaborRateTiers; onDone: (saved: boolean) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const save = useSaveLaborRateTiers();
  const [values, setValues] = useState<Record<TierField, string>>({
    overtimeMultiplier: String(Number(tiers.overtimeMultiplier)),
    emergencyMultiplier: String(Number(tiers.emergencyMultiplier)),
  });
  const [errors, setErrors] = useState<Partial<Record<TierField, string>>>({});
  const [formError, setFormError] = useState('');

  /** A multiplier, or the sentence saying why it is not one — the API's rule, said first. */
  function parse(raw: string): number | string {
    const trimmed = raw.trim();
    const value = Number(trimmed);
    // ⚠️ `Number('')` is 0 (CC-21's trap), so the empty box is refused by name.
    if (trimmed === '' || !Number.isFinite(value) || value < 1 || value > 10) {
      return t('pricebook.tiers.range');
    }
    if (Math.round(value * 100) / 100 !== value) {
      return t('pricebook.tiers.decimals');
    }
    return value;
  }

  function submit() {
    const overtime = parse(values.overtimeMultiplier);
    const emergency = parse(values.emergencyMultiplier);
    const next: Partial<Record<TierField, string>> = {};
    if (typeof overtime === 'string') next.overtimeMultiplier = overtime;
    if (typeof emergency === 'string') next.emergencyMultiplier = emergency;
    setErrors(next);
    setFormError('');
    if (typeof overtime === 'string' || typeof emergency === 'string') return;

    save.mutate(
      { overtimeMultiplier: overtime, emergencyMultiplier: emergency },
      {
        onSuccess: () => onDone(true),
        onError: (err) => {
          if (err instanceof CatalogValidationError) {
            const mapped = {
              overtimeMultiplier: err.forField('OvertimeMultiplier'),
              emergencyMultiplier: err.forField('EmergencyMultiplier'),
            };
            setErrors(mapped);
            if (!Object.values(mapped).some(Boolean)) setFormError(err.message);
            return;
          }
          setFormError(t('pricebook.tiers.saveFailed'));
        },
      },
    );
  }

  const field = (name: TierField, label: string) => (
    <div>
      <label htmlFor={`${id}-${name}`} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={`${id}-${name}`}
        type="number"
        inputMode="decimal"
        min={1}
        max={10}
        step="0.01"
        className={FIELD_CLASS}
        value={values[name]}
        aria-invalid={errors[name] ? true : undefined}
        aria-describedby={errors[name] ? `${id}-${name}-error` : undefined}
        onChange={(event) => setValues((previous) => ({ ...previous, [name]: event.target.value }))}
      />
      {errors[name] ? (
        <p
          id={`${id}-${name}-error`}
          role="alert"
          className="mt-1 text-sm text-[var(--color-destructive)]"
        >
          {errors[name]}
        </p>
      ) : null}
    </div>
  );

  return (
    // noValidate: the browser's own bubble would stop the submit before the
    // sentence the office reads; the rule is said here, and the API keeps it.
    <form
      noValidate
      className="mt-3 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {field('overtimeMultiplier', t('pricebook.tiers.overtimeLabel'))}
        {field('emergencyMultiplier', t('pricebook.tiers.emergencyLabel'))}
      </div>
      {formError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {formError}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? t('pricebook.tiers.saving') : t('pricebook.tiers.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => onDone(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
